/**
 * Dealer Directory filter/sort params (Dealer Directory M8).
 */

/** @typedef {import('../utils/dealerDirectory/dealerDirectoryTypes').DealerDirectoryAvailableFilters} DealerDirectoryAvailableFilters */

/**
 * @typedef {Object} DealerDirectoryFilterParams
 * @property {string|null} q
 * @property {string|null} city
 * @property {string|null} neighborhood
 * @property {boolean} verifiedOnly
 * @property {boolean} hasInventory
 * @property {boolean} contactAvailable
 * @property {string} sort
 * @property {number} page
 * @property {number} pageSize
 */

export const DEALER_DIRECTORY_SORT = {
  RECOMMENDED: "recommended",
  VERIFIED_FIRST: "verified_first",
  MOST_INVENTORY: "most_inventory",
  RECENTLY_UPDATED: "recently_updated",
  ALPHABETICAL: "alphabetical",
};

export const DEALER_DIRECTORY_DEFAULT_SORT = DEALER_DIRECTORY_SORT.RECOMMENDED;

export const DEALER_DIRECTORY_DEFAULT_PAGE_SIZE = 24;

export const DEALER_DIRECTORY_SORT_OPTIONS = [
  { value: DEALER_DIRECTORY_SORT.RECOMMENDED, label: "Recomendados" },
  { value: DEALER_DIRECTORY_SORT.VERIFIED_FIRST, label: "Verificados primero" },
  { value: DEALER_DIRECTORY_SORT.MOST_INVENTORY, label: "Más inventario" },
  { value: DEALER_DIRECTORY_SORT.RECENTLY_UPDATED, label: "Actualizados recientemente" },
  { value: DEALER_DIRECTORY_SORT.ALPHABETICAL, label: "A-Z" },
];

export const DEALER_DIRECTORY_URL_KEYS = [
  "q",
  "city",
  "neighborhood",
  "verifiedOnly",
  "hasInventory",
  "contactAvailable",
  "sort",
  "page",
  "pageSize",
];

export const DEALER_DIRECTORY_FILTER_COPY_ES = {
  heading: "Filtrar concesionarios",
  search: "Buscar concesionario",
  searchPlaceholder: "Nombre, barrio o zona…",
  city: "Zona",
  cityAll: "Todas las zonas",
  neighborhood: "Barrio",
  neighborhoodAll: "Todos los barrios",
  verifiedOnly: "Solo verificados",
  hasInventory: "Con inventario",
  contactAvailable: "Contacto disponible",
  sort: "Ordenar",
  clearFilters: "Limpiar filtros",
  appliedFilters: "Filtros aplicados",
  removeFilterAria: "Quitar filtro",
  filter: "Filtros",
  applyFilters: "Aplicar filtros",
  clear: "Limpiar",
  showResults: "Ver resultados",
};

/** @returns {DealerDirectoryFilterParams} */
export function createDefaultDealerDirectoryFilterParams() {
  return {
    q: null,
    city: null,
    neighborhood: null,
    verifiedOnly: false,
    hasInventory: false,
    contactAvailable: false,
    sort: DEALER_DIRECTORY_DEFAULT_SORT,
    page: 1,
    pageSize: DEALER_DIRECTORY_DEFAULT_PAGE_SIZE,
  };
}

function pickBoolean(value) {
  return value === true || value === "true" || value === "1";
}

function pickPage(value) {
  const num = Number(value);
  if (!Number.isFinite(num) || num < 1) return 1;
  return Math.floor(num);
}

function pickPageSize(value) {
  const num = Number(value);
  if (!Number.isFinite(num) || num < 1) return DEALER_DIRECTORY_DEFAULT_PAGE_SIZE;
  return Math.min(Math.floor(num), 100);
}

function normalizeSort(value) {
  const sort = String(value || "").trim();
  const allowed = new Set(Object.values(DEALER_DIRECTORY_SORT));
  return allowed.has(sort) ? sort : DEALER_DIRECTORY_DEFAULT_SORT;
}

function sanitizeLabel(value, maxLength = 80) {
  if (value == null) return null;
  const trimmed = String(value).trim().slice(0, maxLength);
  if (!trimmed || /[<>]/.test(trimmed)) return null;
  return trimmed;
}

/**
 * @param {URLSearchParams|string|object} input
 * @returns {DealerDirectoryFilterParams}
 */
export function parseDealerDirectoryParams(input) {
  const defaults = createDefaultDealerDirectoryFilterParams();

  if (input && typeof input === "object" && !(input instanceof URLSearchParams)) {
    const raw = input;
    return {
      q: sanitizeLabel(raw.q, 120),
      city: sanitizeLabel(raw.city),
      neighborhood: sanitizeLabel(raw.neighborhood),
      verifiedOnly: pickBoolean(raw.verifiedOnly),
      hasInventory: pickBoolean(raw.hasInventory),
      contactAvailable: pickBoolean(raw.contactAvailable),
      sort: normalizeSort(raw.sort),
      page: pickPage(raw.page),
      pageSize: pickPageSize(raw.pageSize),
    };
  }

  const sp =
    input instanceof URLSearchParams
      ? input
      : new URLSearchParams(typeof input === "string" ? input.replace(/^\?/, "") : "");

  return {
    q: sanitizeLabel(sp.get("q"), 120),
    city: sanitizeLabel(sp.get("city")),
    neighborhood: sanitizeLabel(sp.get("neighborhood")),
    verifiedOnly: pickBoolean(sp.get("verifiedOnly")),
    hasInventory: pickBoolean(sp.get("hasInventory")),
    contactAvailable: pickBoolean(sp.get("contactAvailable")),
    sort: normalizeSort(sp.get("sort")),
    page: pickPage(sp.get("page")),
    pageSize: pickPageSize(sp.get("pageSize")),
  };
}

/**
 * @param {DealerDirectoryFilterParams|object} params
 * @returns {URLSearchParams}
 */
export function serializeDealerDirectoryParams(params) {
  const sp = new URLSearchParams();
  const parsed = parseDealerDirectoryParams(params);
  const defaults = createDefaultDealerDirectoryFilterParams();

  if (parsed.q) sp.set("q", parsed.q);
  if (parsed.city) sp.set("city", parsed.city);
  if (parsed.neighborhood) sp.set("neighborhood", parsed.neighborhood);
  if (parsed.verifiedOnly) sp.set("verifiedOnly", "true");
  if (parsed.hasInventory) sp.set("hasInventory", "true");
  if (parsed.contactAvailable) sp.set("contactAvailable", "true");
  if (parsed.sort && parsed.sort !== defaults.sort) sp.set("sort", parsed.sort);
  if (parsed.page > 1) sp.set("page", String(parsed.page));
  if (parsed.pageSize !== defaults.pageSize) sp.set("pageSize", String(parsed.pageSize));

  return sp;
}

/**
 * @param {URLSearchParams|string|object} current
 * @param {DealerDirectoryFilterParams|object} nextParams
 * @returns {URLSearchParams}
 */
export function mergeDealerDirectorySearchParams(current, nextParams) {
  const sp = new URLSearchParams(
    current instanceof URLSearchParams
      ? current
      : typeof current === "string"
        ? current.replace(/^\?/, "")
        : ""
  );
  DEALER_DIRECTORY_URL_KEYS.forEach((key) => sp.delete(key));
  const serialized = serializeDealerDirectoryParams(nextParams);
  serialized.forEach((value, key) => sp.set(key, value));
  return sp;
}

/**
 * @param {DealerDirectoryFilterParams|object} params
 */
export function countActiveDealerDirectoryFilters(params) {
  const parsed = parseDealerDirectoryParams(params);
  let count = 0;
  if (parsed.q) count += 1;
  if (parsed.city) count += 1;
  if (parsed.neighborhood) count += 1;
  if (parsed.verifiedOnly) count += 1;
  if (parsed.hasInventory) count += 1;
  if (parsed.contactAvailable) count += 1;
  return count;
}

/**
 * @param {DealerDirectoryFilterParams|object} params
 */
export function hasActiveDealerDirectoryFilters(params) {
  return countActiveDealerDirectoryFilters(params) > 0;
}

/** @returns {DealerDirectoryFilterParams} */
export function clearDealerDirectoryFilters() {
  return createDefaultDealerDirectoryFilterParams();
}

/**
 * @param {DealerDirectoryFilterParams|object} params
 * @param {string} key
 */
export function clearDealerDirectoryFilter(params, key) {
  const parsed = parseDealerDirectoryParams(params);
  const next = { ...parsed, page: 1 };

  switch (key) {
    case "q":
      next.q = null;
      break;
    case "city":
      next.city = null;
      next.neighborhood = null;
      break;
    case "neighborhood":
      next.neighborhood = null;
      break;
    case "verifiedOnly":
      next.verifiedOnly = false;
      break;
    case "hasInventory":
      next.hasInventory = false;
      break;
    case "contactAvailable":
      next.contactAvailable = false;
      break;
    default:
      break;
  }

  return next;
}
